<template>
    <v-dialog :value="show" width="600" persistent scrollable>
        <div class="md" v-if="media">
            <div class="md-head">
                <div>
                    <div class="md-title">{{ $t('media.delist.title') }}</div>
                    <div class="md-sub">{{ media.title }}</div>
                </div>
                <v-btn icon small :disabled="isBusy" @click="close">
                    <v-icon size="20">mdi-close</v-icon>
                </v-btn>
            </div>

            <div class="md-body">
                <div v-if="isLoading" class="md-state">
                    <v-progress-circular indeterminate size="28" color="#1e88e5" />
                    <div class="md-state-msg">{{ $t('media.delist.loading') }}</div>
                </div>

                <!-- ⚠ 影響範圍查不到就**不讓下架**。沒有那三個數字的二次確認等於沒有
                     二次確認——老師會在不知道自己停用了誰的課程的情況下按下去。 -->
                <div v-else-if="loadError" class="md-state">
                    <v-icon size="30" color="#e57373">mdi-alert-circle-outline</v-icon>
                    <div class="md-state-msg">{{ loadError }}</div>
                    <v-btn small outlined class="normal-case" @click="load">
                        {{ $t('media.picker.retry') }}
                    </v-btn>
                </div>

                <template v-else-if="impact">
                    <!-- 沒人在用：最單純的情況，不要用嚇人的紅字 -->
                    <div v-if="impact.quest_count === 0" class="md-none">
                        {{ $t('media.delist.nobody_uses') }}
                    </div>

                    <template v-else>
                        <div class="md-warn">
                            <v-icon size="20" color="#c62828">mdi-alert</v-icon>
                            <div>{{ $t('media.delist.will_block',
                                { q: impact.quest_count, u: impact.unit_count }) }}</div>
                        </div>

                        <!-- ⚠⚠ **這個數字是 impact 端點存在的主要理由**（規格 §5.2）。
                             素材是下架者的、最終決定權歸他，但他**必須知道自己正在停用
                             別人的課程**。0 的時候不顯示（那是雜訊）。 -->
                        <div v-if="impact.not_mine_quest_count > 0" class="md-notmine">
                            {{ $t('media.delist.not_mine',
                                { n: impact.not_mine_quest_count }) }}
                        </div>

                        <!-- ⚠ 一定要講清楚通知怎麼走（B12 於 2026-09-09 完成）：
                             受影響的老師會收到站內訊息與 email，下架者自己也會收到摘要。
                             ⚠ 這一句在 B12 之前寫的是「系統還不會通知」——功能上線了
                             就變成錯的，所以文案與 key 名（no_notify_yet → notify_note）
                             都一起改。 -->
                        <div class="md-nonotify">{{ $t('media.delist.notify_note') }}</div>

                        <!-- 受影響的範本與題目。⚠ 只列前幾筆：清單太長會把下面的按鈕推出畫面，
                             而老師要的是「規模感」而不是逐筆清點。 -->
                        <div class="md-list">
                            <div v-for="hw in shownHomeworks" :key="hw.diary_homework_id"
                                class="md-hw">
                                <div class="md-hw-head">
                                    <span class="md-hw-id">
                                        {{ $t('media.delist.homework_n',
                                            { n: hw.diary_homework_id }) }}
                                    </span>
                                    <span v-if="!hw.is_mine" class="md-tag-other">
                                        {{ $t('media.delist.not_yours') }}
                                    </span>
                                </div>
                                <div v-for="q in hw.quests" :key="q.quest_id" class="md-quest">
                                    <span v-if="q.unit !== null" class="md-unit">
                                        {{ $t('media.delist.unit_n', { n: q.unit }) }}
                                    </span>
                                    {{ q.quest_title || $t('unit.settings.untitled') }}
                                </div>
                            </div>
                            <div v-if="hiddenHomeworkCount > 0" class="md-more">
                                {{ $t('media.delist.more_homeworks',
                                    { n: hiddenHomeworkCount }) }}
                            </div>
                        </div>
                    </template>

                    <!-- ⚠ **文案不能寫「刪除」**（ADR 0007）：S3 物件是 public-read，
                         檔案不會從網路上消失。也要講出「可以恢復」——那是這個動作
                         與刪除最重要的差別。 -->
                    <div class="md-facts">
                        <div>{{ $t('media.delist.fact_not_deleted') }}</div>
                        <div>{{ $t('media.delist.fact_reversible') }}</div>
                    </div>

                    <!-- ⚠ 有影響時才要求打勾。沒人在用的話那個閘門只是多一步。 -->
                    <v-checkbox v-if="impact.quest_count > 0" v-model="agreed" dense hide-details
                        class="md-agree" :disabled="isBusy">
                        <template #label>
                            <span class="md-agree-lbl">{{ $t('media.delist.agree') }}</span>
                        </template>
                    </v-checkbox>

                    <div v-if="submitError" class="md-err">{{ submitError }}</div>
                </template>
            </div>

            <div class="md-foot">
                <v-btn text class="normal-case" :disabled="isBusy" @click="close">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn color="error" depressed class="normal-case"
                    :disabled="!canSubmit" :loading="isBusy" @click="submit">
                    {{ $t('media.library.delist') }}
                </v-btn>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import { mediaServices } from '@/services/media';

/// 最多列幾個範本。⚠ 這是**呈現**的上限：老師要的是規模感，不是逐筆清點，
/// 而清單太長會把「下架」按鈕推出畫面外。
const MAX_HOMEWORKS = 5;

/**
 * 下架前的二次確認 — 見 mangachat-studio-api `docs/plans/MediaLibrary.spec.md`
 * §2.4.3、§5.2、[ADR 0009](../../../../../../mangachat-studio-api/docs/adr/0009-delisting-blocks-quests-instead-of-deleting-them.md)。
 *
 * ⚠⚠ **這不是「新題目選不到而已」。** 引用這份素材的題目會**當場被停用**，而那些題目
 * 可能是**別的老師**的。所以這個對話框有三件事非做不可：
 *
 * 1. **先查影響範圍**（`getMediaImpact`），查不到就**不讓下架**。
 * 2. **講出「其中 M 筆不是你的」**——那是 impact 端點存在的主要理由（規格 §5.2）。
 *    素材是下架者的、最終決定權歸他，但他必須知道自己正在停用別人的課程。
 * 3. **講出通知怎麼走**（B12）。受影響的老師會收到站內訊息與 email——老師需要知道
 *    「對方會不會知道」才能決定要不要先跟對方講一聲。
 *
 * ⚠ **文案不能寫「刪除」或「移除」**（ADR 0007）：S3 物件是 `public-read`，檔案不會從
 * 網路上消失。下架的效果是「庫裡挑不到、用到它的題目被停用」。
 *
 * ⚠ **`confirmed: true` 是後端的硬要求**，沒帶回 409。所以打勾那個閘門不是裝飾。
 */
export default {
    name: 'MediaDelistDialog',
    props: {
        show: { type: Boolean, default: false },
        /** 要下架的素材。⚠ 呼叫端要先確認 `is_mine`（後端對別人的素材回 403）。 */
        media: { type: Object, default: null },
    },
    data() {
        return {
            impact: null,
            agreed: false,
            isLoading: false,
            isBusy: false,
            loadError: '',
            submitError: '',
        };
    },
    computed: {
        shownHomeworks() {
            const list = (this.impact && this.impact.homeworks) || [];
            return list.slice(0, MAX_HOMEWORKS);
        },
        hiddenHomeworkCount() {
            const list = (this.impact && this.impact.homeworks) || [];
            return Math.max(0, list.length - MAX_HOMEWORKS);
        },
        canSubmit() {
            /// ⚠ **查不到影響範圍就不能送**（見檔頭第 1 點）。
            if (!this.impact || this.isBusy || this.isLoading || this.loadError) {
                return false;
            }
            /// 沒人在用就不需要打勾；有影響時那個閘門是必要的。
            return this.impact.quest_count === 0 ? true : this.agreed;
        },
    },
    watch: {
        /**
         * ⚠ `immediate` 不能省：呼叫端用 `v-if` 建立時，元件掛載當下 `show` 已經是 true，
         * 一般的 watcher 不會觸發，於是永遠不去查影響範圍。
         * （同一個坑在 MediaPicker / MediaUploadDialog / MediaEditDialog / WordlistDialog /
         * HotspotEditDialog 都踩過。）
         */
        show: {
            immediate: true,
            handler(opened) {
                if (opened) {
                    this.reset();
                    this.load();
                }
            },
        },
    },
    methods: {
        reset() {
            this.impact = null;
            /// ⚠ **每次開啟都要清掉打勾**。沿用上一次的確認是不能接受的——
            /// 那等於讓第二個素材免費通過二次確認。
            this.agreed = false;
            this.isLoading = false;
            this.isBusy = false;
            this.loadError = '';
            this.submitError = '';
        },

        async load() {
            if (!this.media) {
                return;
            }
            this.isLoading = true;
            this.loadError = '';
            try {
                this.impact = await mediaServices.getMediaImpact(this.media.media_id);
            } catch (err) {
                /// ⚠ 這個 catch 不能省，而且失敗時要**擋住下架**（見 canSubmit）。
                this.impact = null;
                this.loadError = this.$t('media.delist.err_impact');
            } finally {
                this.isLoading = false;
            }
        },

        async submit() {
            if (!this.canSubmit) {
                return;
            }
            this.isBusy = true;
            this.submitError = '';
            try {
                const res = await mediaServices.delistMedia(this.media.media_id);
                this.$emit('delisted', res);
                this.$emit('update:show', false);
            } catch (err) {
                const status = err && err.status;
                if (status === 403) {
                    this.submitError = this.$t('media.delist.err_forbidden');
                } else if (status === 409) {
                    /// ⚠ 理論上到不了這裡（service 固定帶 `confirmed: true`），
                    /// 但留著才看得出「後端要求確認」這件事真的成立。
                    this.submitError = this.$t('media.delist.err_need_confirm');
                } else {
                    console.log('delistMedia failed:', err && err.message);
                    this.submitError = this.$t('media.delist.err_failed');
                }
            } finally {
                this.isBusy = false;
            }
        },

        close() {
            if (this.isBusy) {
                return;
            }
            this.$emit('update:show', false);
        },
    },
};
</script>

<style scoped>
.md {
    background: #fff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    max-height: 88vh;
}

.md-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px 20px 12px;
    border-bottom: 1px solid #eceff1;
}

.md-title {
    font-size: 17px;
    font-weight: 600;
    color: #263238;
}

.md-sub {
    font-size: 12px;
    color: #78909c;
    margin-top: 2px;
    word-break: break-all;
}

.md-body {
    padding: 16px 20px;
    overflow-y: auto;
}

.md-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 24px 0;
}

.md-state-msg {
    font-size: 13px;
    color: #607d8b;
    text-align: center;
}

.md-none {
    font-size: 13px;
    color: #37474f;
    line-height: 1.6;
    padding: 4px 0 8px;
}

/* 有影響時的主訊息：紅底，因為後果是別人的課程被停用 */
.md-warn {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    background: #ffebee;
    border-radius: 4px;
    font-size: 13px;
    color: #b71c1c;
    line-height: 1.6;
    font-weight: 500;
}

/* 「其中 M 筆不是你的」——比主訊息更重要，所以更醒目 */
.md-notmine {
    margin-top: 8px;
    padding: 10px 12px;
    background: #fff3e0;
    border-left: 3px solid #ef6c00;
    border-radius: 4px;
    font-size: 13px;
    color: #6d4c41;
    line-height: 1.6;
    font-weight: 600;
}

.md-nonotify {
    margin-top: 8px;
    font-size: 12px;
    color: #78909c;
    line-height: 1.6;
}

.md-list {
    margin-top: 12px;
    max-height: 30vh;
    overflow-y: auto;
    border: 1px solid #eceff1;
    border-radius: 4px;
    padding: 8px 10px;
}

.md-hw + .md-hw {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #eceff1;
}

.md-hw-head {
    display: flex;
    align-items: center;
    gap: 8px;
}

.md-hw-id {
    font-size: 12px;
    font-weight: 600;
    color: #455a64;
}

.md-tag-other {
    font-size: 11px;
    color: #e65100;
    background: #fff3e0;
    border-radius: 3px;
    padding: 1px 6px;
}

.md-quest {
    font-size: 12px;
    color: #546e7a;
    margin-top: 3px;
    padding-left: 2px;
}

.md-unit {
    color: #90a4ae;
    margin-right: 4px;
}

.md-more {
    font-size: 11px;
    color: #90a4ae;
    margin-top: 8px;
}

.md-facts {
    margin-top: 12px;
    font-size: 12px;
    color: #607d8b;
    line-height: 1.7;
}

.md-agree {
    margin-top: 10px;
}

.md-agree-lbl {
    font-size: 13px;
    color: #263238;
    line-height: 1.5;
}

.md-err {
    margin-top: 10px;
    padding: 8px 10px;
    background: #ffebee;
    border-radius: 4px;
    font-size: 12px;
    color: #e53935;
    line-height: 1.5;
}

.md-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid #eceff1;
}
</style>
